<script setup lang="ts">
import type { Content, Instance } from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import { computed, onMounted, useSlots, useTemplateRef } from "vue";
import type { TippyOptions } from "vue-tippy";
import { Tippy as VueTippy } from "vue-tippy";

import { tooltipDelayToAppear } from "./internal/baseTooltip";

interface TooltipProps {
  position?: TippyOptions["placement"];
  appendTo?: TippyOptions["appendTo"];
  showOnCreate?: boolean;
  interactive?: boolean;
  delay?: number | [number, number];
  duration?: number | [number, number];
  offset?: TippyOptions["offset"];
  trigger?: TippyOptions["trigger"];
  triggerRole?: string;
  onlyShowOnOverflow?: boolean; // If true, only show the tooltip when the content of #default is overflowing the container
  onShow?: TippyOptions["onShow"];
  content?: Content;
  maxWidth?: number | string;
  showWhenClipped?: boolean; // Show tooltip even when reference is inside a clipped/teleported container
}

const props = withDefaults(defineProps<TooltipProps>(), {
  appendTo: () => document.body,
  interactive: false,
  delay: () => [tooltipDelayToAppear, 50],
  duration: 150,
  showOnCreate: false,
  position: "top",
  offset: () => [0, 10] as [number, number], // Because TS infers [0,10] as `number[]`
  trigger: undefined,
  triggerRole: undefined,
  onlyShowOnOverflow: false,
  onShow: undefined,
  content: undefined,
  maxWidth: undefined,
  showWhenClipped: false,
});

const tooltipRef = useTemplateRef<typeof VueTippy>("tooltip");

const slots = useSlots();

const popperOptions = computed(() =>
  props.showWhenClipped ? { modifiers: [{ name: "hide", enabled: false }] } : undefined,
);

onMounted(() => {
  // Apply a role to the tooltip's "trigger" element if one is given
  // (a 'role' passed to VueTippy is given to the tooltip itself)
  if (tooltipRef.value?.elem && props.triggerRole) {
    tooltipRef.value.elem.setAttribute("role", props.triggerRole);
  }
});

/**
 * Check if the tooltip should be shown, used when onlyShowOnOverflow is set
 * to true. In that case, the tooltip will only be shown when the content of
 * #default is overflowing the container.
 */
const shouldShow = (instance: Instance): false | void => {
  // Check for any onShow in the props first -> if it's already false,
  // don't show the tooltip. Otherwise, continue with the check
  if (props.onShow?.(instance) === false) return false;

  // If there's no content (ie. what gets rendered in the tooltip), don't show the tooltip
  if (!slots.content && !props.content) return false;

  // If onlyShowOnOverflow isn't set, always render the tooltip
  if (!props.onlyShowOnOverflow) return;

  // Check if the element is overflowing
  // If not, return false (ie. don't show the tooltip)
  const element = instance.reference;
  if (element.scrollWidth > element.clientWidth) return;
  if (element.scrollHeight > element.clientHeight) return;
  return false;
};
</script>

<template>
  <VueTippy
    ref="tooltip"
    tag="span"
    data-testid="tooltip"
    class="base-tooltip"
    :placement="position"
    :delay
    :duration
    :interactive
    :append-to
    :offset
    :trigger
    :show-on-create
    :on-show="shouldShow"
    :content
    :max-width
    :popper-options
  >
    <template #default>
      <slot />
    </template>
    <template v-if="$slots.content" #content>
      <slot name="content" />
    </template>
  </VueTippy>
</template>

<style lang="scss" scoped>
.base-tooltip {
  display: inline-block;
  max-width: 100%;
}
</style>
